import { createActions } from '@manager-base/app-base/config/data-access';
import { BASE_PATH_API } from '@manager-base/app-base/environment/paths/constants';

const actions = {
    /**
     * @param {number} params.id - the building id
     * @returns {PropertyDTO} PropertyDTO
     */
    get: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:Id`
    },

    save: {
        method: 'POST',
        url: `${BASE_PATH_API}/properties(/:id)`
    },

    getWithEpay: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/withEpay`,
        isArray: true
    },

    query: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/list`,
        isArray: true
    },
    getUnitCounts: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/units/count`,
        isArray: true
    },
    queryWithCache: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/list`,
        isArray: true,
        cache: true
    },
    queryGrid: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/list`,
        params: {
            '$count': true
        }
    },
    getProperties: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties`,
        params: {
            '$count': true
        }
    },
    getPreferredVendors: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/preferredVendors`,
    },
    /**
     * @param {number} params.id - the building id
     * @returns {PropertyDTO} PropertyDTO
     */
    update: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:id`
    },
    updateBulk: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/bulk`,
    },
    updatePropertyAndUnits: {
        url: `${BASE_PATH_API}/properties/:id/batch`,
        method: 'POST'
    },
    updateDepositTrustAccount: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:id/depositTrustAccount/:depositTrustAccountId`
    },
    /**
     * @param {number} params.id - the building id
     * @param {PropertyImageDto} postData
     */
    addImage: {
        method: 'POST',
        url: `${BASE_PATH_API}/properties/:id/images`
    },

    reorderImages: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:id/images`
    },

    /**
     * Gets the current residents for each unit (along with unit id, unit
     * number, size and lease id) for the property.
     */
    tenancy: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/tenancy`,
        isArray: true
    },

    /**
     * Gets all allocations for the property.
     */
    commonallocations: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/commonallocations`,
        isArray: true
    },

    /**
     * Gets all units for the property.
     */
    getUnits: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/units/all`,
        isArray: true
    },

    /**
     * Gets all units for the property
     * but actually includes lease data
     */
    getUnitsRollup: {
        method: 'GET',
        url: `${BASE_PATH_API}/units/rollup(/:id)`,
        isArray: true
    },
    /**
     * Gets all units for the property
     * Executes a check to see if the users
     * have access to the building without
     * using the cache to retrieve building ids
     */
    getUnitsRollupNoCache: {
        method: 'GET',
        url: `${BASE_PATH_API}/units/rollupNoCache(/:id)`,
        isArray: true
    },
    /**
     * get|create|update|delete unit details
     */
    getUnit: {
        method: 'GET',
        url: `${BASE_PATH_API}/units(/:id)`
    },

    getUnitAddressForLedger: {
        method: 'GET',
        url: `${BASE_PATH_API}/units/ledger/address(/:id)`
    },

    createUnit: {
        method: 'POST',
        url: `${BASE_PATH_API}/units`
    },

    createUnits: {
        method: 'POST',
        url: `${BASE_PATH_API}/units/bulkCreate`,
        isArray: true
    },

    updateUnit: {
        method: 'PUT',
        url: `${BASE_PATH_API}/units(/:id)`
    },

    deleteUnit: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/units(/:id)`
    },

    saveUnit: {
        method: 'POST',
        url: `${BASE_PATH_API}/units`
    },

    getUnitLimitEnforcementEligibility: {
        method: 'GET',
        url: `${BASE_PATH_API}/units/limitEnforcement/accountEligibility`
    },

    getSubscriptionCounts: {
        method: 'GET',
        url: `${BASE_PATH_API}/units/subscriptionCounts`
    },

    getBuildingTypeSubscriptionCounts: {
        method: 'GET',
        url: `${BASE_PATH_API}/units/:buildingType/subscriptionCounts`
    },
    /**
     * Get the grouped property data used by the Property Filter UI
     */
    propertiesInGroups: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/inCategories`,
        isArray: true
    },

    /**
     * Get the grouped property data for properties with owners used by the Property Filter UI
     */
    propertiesInGroupsWithOwners: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/inCategories`,
        isArray: true
    },

    propertiesFilterReady: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/inCategories`,
        isArray: true,
        transformResponse: function transform(data) {
            return data.map(function setNumericIds(properties) {
                // api/properties/inCategories returns Id with a leading underscore
                // in order to match when filtering by Id we need to remove that underscore
                properties.Items.forEach(function idOrNull(property) {
                    const id = property.Id.match(/\d+/);

                    // filterId will intentionally be undefined for special values like 'All properties'
                    property.filterId = id ? parseInt(id[0], 10) : id;
                });

                return properties;
            });
        }
    },

    getPropertiesWithBudget: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/withBudget`,
        isArray: true
    },

    getPropertyPreferredVendorIds: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/preferredVendors`,
        isArray: true
    },

    updatePreferredVendors: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:id/preferredVendors`,
        isArray: true
    },
    doesPropertyContainRecurringNonLeaseTransaction: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/recurringExpenses`
    },
    getPropertySavedCreditCardMethods: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/savedCreditCardMethods`
    },
    doesPropertyContainJournalEntries: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/hasJournalEntries`
    },
    isSubscriberWithinMaximumNumberOfUnitsAllowed: {
        method: 'GET',
        url: `${BASE_PATH_API}/account/:propertyId/isWithinMaxNumberOfUnitsAllowed`
    },
    deleteProperty: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/properties/:propertyId`
    },
    getPropertyAppliances: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/appliances`,
        isArray: true
    },
    getUnitAppliances: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/units/:unitId/appliances`,
        isArray: true
    },
    lookupProperties: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/lookups`
    },
    getPropertyAccountBalance: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/accountBalance`
    },
    getUnitList: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/unitsList`,
        isArray: true
    },
    getPropertyFeatures: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/features`,
        isArray: true
    },
    getUnitFeatures: {
        method: 'GET',
        url: `${BASE_PATH_API}/units/features`,
        isArray: true
    },
    getIncludedInRentUtilities: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/includedInRentUtilities`,
        isArray: true
    },
    getTenantWebsiteSettings: {
        method: 'GET',
        url: `${BASE_PATH_API}/settings/:propertyId/resident`,
        isArray: true
    },
    saveTenantWebsiteSettings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/settings/:propertyId/resident`
    },
    getVacantUnits: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/vacantUnits`
    },
    //listing status
    getUnitsStatus: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:id/units/status`
    },
    //actual unit status
    getPropertyUnitsWithStatus: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/units/statuses`
    },
    updatePropertyUnitStatuses: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:propertyId/units/statuses`
    },
    getUnitSummary: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/units/:unitId`
    },
    updateUnitInfo: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/units/:unitId`
    },
    deleteUnitInfo: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/properties/units/:unitId`
    },
    inactivateProperty: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:id/inactivate`
    },
    reactivateProperty: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:id/reactivate`
    },
    getUnitLeases: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/units/:unitId/leases`,
        isArray: true
    },
    getApplicantsByUnit: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/units/:unitId/applicants`,
        isArray: true
    },
    getPropertyUnits: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/units`,
        params: {
            '$count': true
        }
    },
    getUsersHasAccessToProperty: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/users`,
        isArray: true
    },
    getUnitMeterReadings: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/meterReadings/list`,
        params: {
            '$count': true
        }
    },
    deleteMeterReadings: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/properties/:propertyId/meterReadings?readingDate=:readingDate&meterTypeId=:meterTypeId`
    },
    getMeterReadingDetails: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/meterReadings?readingDate=:readingDate&meterTypeId=:meterTypeId`
    },
    createMeterReadings: {
        method: 'POST',
        url: `${BASE_PATH_API}/properties/:propertyId/meterReadings`
    },
    editMeterReadings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:propertyId/meterReadings`
    },
    getResidentMeterReadings: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/meterReadings/tenants`
    },
    updatePaymentMethodSettings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:propertyId/paymentSettings`
    },
    getPaymentReminderSettings: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/paymentReminders`
    },
    updatePaymentReminderSettings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/properties/:propertyId/paymentReminders`
    },
    deletePaymentReminderSettings: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/properties/:propertyId/paymentReminders`
    },
    getPrepaymentSettings: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/prepaymentSettings`
    },
    getPaymentAllocationSettings: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/paymentAllocationSettings`
    },
    updatePropertyPaymentSettings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/paymentMethodSettings`
    },
    getIncomeStatement: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/incomeStatement`,
        params: {
            '$count': true
        }
    },
    getCountryInfoForScreeningByProperty: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/:propertyId/countryInfo`
    },
    getMailingLayouts: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/mailingLayouts`
    },
    getMailingSenders: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/mailingSenders`
    },
    getLockedPeriod: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/lockedPeriod/:propertyId`
    },
    getDefaultManagementFeePolicy: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/managementFeePolicy`
    },
    getLeasesForProperties: {
        method: 'GET',
        url: `${BASE_PATH_API}/properties/leases`,
        isArray: true
    },
    doesPropertyHaveAnyCalendars: {
        method: 'GET',
        url: `${BASE_PATH_API}/v1/properties/:propertyId/calendars/anyCalendarsExist`
    },
    updateUnitComment: {
        method: 'PUT',
        url: `${BASE_PATH_API}/units/comment`
    }
};

export const PropertiesApi = function PropertiesApi(
    $httpParamSerializer
) {
    const service = createActions(actions);

    service.getRentalPropertiesExportUrl = function getRentalPropertiesExportUrl(params) {
        return `${BASE_PATH_API}/properties/export?${$httpParamSerializer(params)}`;
    };

    service.exportPropertyUnitsUrl = function getPropertyUnitsExportUrl(propertyId, params) {
        return `${BASE_PATH_API}/properties/${propertyId}/units/export?${$httpParamSerializer(params)}`;
    };

    service.printIncomeStatement = function printIncomeStatement(propertyId, params) {
        return `${BASE_PATH_API}/properties/${propertyId}/incomeStatement/export?${$httpParamSerializer(params)}`;
    };

    service.printIncomeStatementByUnit = function printIncomeStatementByUnit(propertyId, params) {
        return `${BASE_PATH_API}/properties/${propertyId}/incomeStatementByUnit/export?${$httpParamSerializer(params)}`;
    };

    return service;
};
PropertiesApi.$inject = [
    '$httpParamSerializer'
];
